/**
 * Navigation Module
 *
 * API methods for menus and navigation.
 */

import { get } from '../core/client';
import { getConfig } from '../core/config';
import { buildEndpoint } from '../core/endpoint';
import type { SDKResponse, Menu } from '../types';
import type { NavigationMenuSlot } from '@shoppex/contracts/navigation';
import { getNavigationMenuTitles } from '@shoppex/contracts/navigation';

interface MenusResponse {
  menus: Menu[];
}

interface MenuResponse {
  menu: Menu;
}

const NAVIGATION_CACHE_TTL = 5 * 60 * 1000;

/**
 * Get all menus for the store
 */
export async function getMenus(): Promise<SDKResponse<Menu[]>> {
  const config = getConfig();
  const response = await get<MenusResponse>(
    buildEndpoint('/v1/storefront/shops/name/:storeSlug/menus', {
      storeSlug: config.storeSlug,
    }),
    {
      cache: {
        key: `menus:${config.storeSlug}`,
        ttl: NAVIGATION_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data.menus,
    };
  }

  return {
    success: false,
    message: response.message,
  };
}

/**
 * Get a menu by its exact title.
 */
export async function getMenuByTitle(title: string): Promise<SDKResponse<Menu>> {
  const config = getConfig();
  const response = await get<MenuResponse>(
    buildEndpoint('/v1/storefront/shops/name/:storeSlug/menus/:title', {
      storeSlug: config.storeSlug,
      title,
    }),
    {
      cache: {
        key: `menu:${config.storeSlug}:${title}`,
        ttl: NAVIGATION_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data.menu,
    };
  }

  return {
    success: false,
    message: response.message,
  };
}

/**
 * Get a menu by its exact title.
 */
export async function getMenu(title: string): Promise<SDKResponse<Menu>> {
  return getMenuByTitle(title);
}

/**
 * Get a menu by canonical slot. The backend resolves legacy menu titles too.
 */
export async function getMenuBySlot(slot: NavigationMenuSlot): Promise<SDKResponse<Menu>> {
  const config = getConfig();
  const response = await get<MenuResponse>(
    buildEndpoint('/v1/storefront/shops/name/:storeSlug/menus/:title', {
      storeSlug: config.storeSlug,
      title: slot,
    }),
    {
      cache: {
        key: `menu-slot:${config.storeSlug}:${slot}`,
        ttl: NAVIGATION_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data.menu,
    };
  }

  return {
    success: false,
    message: response.message,
  };
}

export function getMenuSlotTitles(slot: NavigationMenuSlot): string[] {
  return getNavigationMenuTitles(slot);
}
